import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ClipboardList, Search, X, Loader2, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const PAGE_SIZE = 100;

const UploadedPickSlipsReport = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [slipType, setSlipType] = useState('all');     // all | regular | seasonal
  const [statusFilter, setStatusFilter] = useState('all'); // all | DC_LINKED | DISPATCHED | PACKED | PICKED | PARTIAL | NOT_PICKED | CANCELLED
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const buildParams = (pageArg, overrides = {}) => {
    const searchArg = overrides.search !== undefined ? overrides.search : search;
    const fromArg = overrides.dateFrom !== undefined ? overrides.dateFrom : dateFrom;
    const toArg = overrides.dateTo !== undefined ? overrides.dateTo : dateTo;
    const typeArg = overrides.slipType !== undefined ? overrides.slipType : slipType;
    const statusArg = overrides.statusFilter !== undefined ? overrides.statusFilter : statusFilter;
    return {
      q: searchArg || undefined,
      date_from: fromArg || undefined,
      date_to: toArg || undefined,
      slip_type: typeArg && typeArg !== 'all' ? typeArg : undefined,
      state: statusArg && statusArg !== 'all' ? statusArg : undefined,
      page: pageArg,
      page_size: PAGE_SIZE,
    };
  };

  const fetchReport = useCallback(async (pageArg = 1, overrides = {}) => {
    setLoading(true);
    try {
      const r = await apiClient.get('/wms/fg/pick-slips/uploaded-report', {
        params: buildParams(pageArg, overrides),
      });
      const d = r.data || {};
      setRows(d.rows || []);
      setTotal(d.total || 0);
      setPage(d.page || pageArg);
      setTotalPages(d.total_pages || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
      setRows([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [search, dateFrom, dateTo, slipType, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchReport(1, { search: '', dateFrom: '', dateTo: '', slipType: 'all', statusFilter: 'all' }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => fetchReport(1);
  const handleClear = () => {
    setSearch(''); setDateFrom(''); setDateTo(''); setSlipType('all'); setStatusFilter('all');
    fetchReport(1, { search: '', dateFrom: '', dateTo: '', slipType: 'all', statusFilter: 'all' });
  };
  const goPage = (p) => { if (p >= 1 && p <= totalPages) fetchReport(p); };

  // Type / status selects re-run the query immediately (server-side).
  const onTypeChange = (v) => { setSlipType(v); fetchReport(1, { slipType: v }); };
  const onStatusChange = (v) => { setStatusFilter(v); fetchReport(1, { statusFilter: v }); };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = buildParams(1);
      delete params.page;
      delete params.page_size;
      params.format = 'csv';
      const r = await apiClient.get('/wms/fg/pick-slips/uploaded-report', {
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `uploaded_pick_slips_skuwise_${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const cell = (v) => (v || v === 0 ? v : <span className="text-gray-300">—</span>);

  const STATUS_STYLE = {
    DC_LINKED: 'bg-blue-100 text-blue-700',
    DISPATCHED: 'bg-indigo-100 text-indigo-700',
    PACKED: 'bg-teal-100 text-teal-700',
    PICKED: 'bg-green-100 text-green-700',
    PARTIAL: 'bg-amber-100 text-amber-700',
    NOT_PICKED: 'bg-rose-100 text-rose-700',
    CANCELLED: 'bg-gray-200 text-gray-600',
  };
  const STATUS_LABEL = {
    DC_LINKED: 'DC Linked', DISPATCHED: 'Dispatched', PACKED: 'Packed',
    PICKED: 'Picked', PARTIAL: 'Partial', NOT_PICKED: 'Not Picked', CANCELLED: 'Cancelled',
  };
  const StatusBadge = ({ state }) => (
    <Badge className={`border-0 text-[11px] ${STATUS_STYLE[state] || 'bg-slate-100 text-slate-700'}`}>
      {STATUS_LABEL[state] || state || '—'}
    </Badge>
  );

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="uploaded-pick-slips-report-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-[110rem] mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button variant="ghost" onClick={() => navigate('/wms/fg')} className="text-[#F9FAFB] hover:bg-white/10 mr-3" data-testid="upr-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <ClipboardList className="w-7 h-7 mr-3" />
          <div>
            <h1 className="text-xl font-bold font-display">Uploaded Pick Slips — SKU-wise</h1>
            <p className="text-sm text-[#E8DCC8]">All FG pick slips (Regular + Seasonal) · box/bundle-aware Status &amp; DC No · upload &amp; pick date/time (IST), user</p>
          </div>
        </div>
      </header>

      <main className="max-w-[110rem] mx-auto px-4 md:px-6 py-8 space-y-6">
        {/* Filters */}
        <Card>
          <CardContent className="pt-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-[#5A5F6E] mb-1">Global filter</label>
              <div className="flex gap-2">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                  placeholder="Search pick slip #, customer, SKU / barcode, design, uploaded-by, picker…"
                  className="flex-1"
                  data-testid="upr-search-input"
                />
                <Button onClick={handleSearch} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="upr-search-btn">
                  <Search className="w-4 h-4" />
                </Button>
                <Button onClick={handleClear} variant="outline" className="border-[#E5DFD0]" data-testid="upr-clear-btn">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:items-end gap-3 flex-wrap">
              <div>
                <label className="block text-xs font-medium text-[#5A5F6E] mb-1">Type</label>
                <Select value={slipType} onValueChange={onTypeChange}>
                  <SelectTrigger className="w-40" data-testid="upr-type-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="regular">Regular</SelectItem>
                    <SelectItem value="seasonal">Seasonal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#5A5F6E] mb-1">Status</label>
                <Select value={statusFilter} onValueChange={onStatusChange}>
                  <SelectTrigger className="w-44" data-testid="upr-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="DC_LINKED">DC Linked</SelectItem>
                    <SelectItem value="DISPATCHED">Dispatched</SelectItem>
                    <SelectItem value="PACKED">Packed</SelectItem>
                    <SelectItem value="PICKED">Picked</SelectItem>
                    <SelectItem value="PARTIAL">Partial</SelectItem>
                    <SelectItem value="NOT_PICKED">Not Picked</SelectItem>
                    <SelectItem value="CANCELLED">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="block text-xs font-medium text-[#5A5F6E] mb-1">Uploaded from (IST)</label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="upr-date-from" />
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="block text-xs font-medium text-[#5A5F6E] mb-1">Uploaded to (IST)</label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="upr-date-to" />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSearch} variant="outline" className="border-[#E5DFD0]" data-testid="upr-apply-btn">
                  Apply
                </Button>
                <Button onClick={handleExport} disabled={exporting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="upr-export-csv-btn">
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Export CSV
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 inline-block">
          <p className="text-xs text-[#5A5F6E]">Matching rows (SKU lines)</p>
          <p className="text-2xl font-bold mt-1 text-teal-700" data-testid="upr-summary-total">{total.toLocaleString()}</p>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="upr-report-table">
                <thead>
                  <tr className="text-left text-[#5A5F6E] bg-gray-50 border-b whitespace-nowrap">
                    <th className="px-3 py-2.5 font-medium">Type</th>
                    <th className="px-3 py-2.5 font-medium">Pick Slip #</th>
                    <th className="px-3 py-2.5 font-medium">Customer</th>
                    <th className="px-3 py-2.5 font-medium">SKU / Barcode</th>
                    <th className="px-3 py-2.5 font-medium">Design</th>
                    <th className="px-3 py-2.5 font-medium">Shade</th>
                    <th className="px-3 py-2.5 font-medium">Size</th>
                    <th className="px-3 py-2.5 font-medium">Box</th>
                    <th className="px-3 py-2.5 font-medium text-right">Req Qty</th>
                    <th className="px-3 py-2.5 font-medium text-right">Qty</th>
                    <th className="px-3 py-2.5 font-medium">Status</th>
                    <th className="px-3 py-2.5 font-medium">DC No</th>
                    <th className="px-3 py-2.5 font-medium">Uploaded At (IST)</th>
                    <th className="px-3 py-2.5 font-medium">Uploaded By</th>
                    <th className="px-3 py-2.5 font-medium">Picked At (IST)</th>
                    <th className="px-3 py-2.5 font-medium">Picker</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={16} className="px-3 py-10 text-center text-[#5A5F6E]">
                      <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
                    </td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={16} className="px-3 py-10 text-center text-[#5A5F6E]" data-testid="upr-empty">
                      No pick slip lines found.
                    </td></tr>
                  ) : rows.map((r, i) => (
                    <tr key={`${r.pick_slip_number}-${r.sku_code}-${r.box_number || ''}-${i}`} className="border-b hover:bg-gray-50" data-testid={`upr-row-${i}`}>
                      <td className="px-3 py-2">
                        <Badge className={`border-0 text-[11px] ${r.type === 'Seasonal' ? 'bg-lime-100 text-lime-800' : 'bg-slate-100 text-slate-700'}`}>
                          {r.type}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 font-mono font-semibold text-teal-700">{cell(r.pick_slip_number)}</td>
                      <td className="px-3 py-2">{cell(r.customer)}</td>
                      <td className="px-3 py-2 font-mono">{cell(r.sku_code)}</td>
                      <td className="px-3 py-2">{cell(r.design_name)}</td>
                      <td className="px-3 py-2">{cell(r.shade)}</td>
                      <td className="px-3 py-2">{cell(r.size)}</td>
                      <td className="px-3 py-2 font-mono text-xs" data-testid={`upr-box-${i}`}>{cell(r.box_number)}</td>
                      <td className="px-3 py-2 text-right font-semibold">{r.required_qty}</td>
                      <td className="px-3 py-2 text-right font-semibold">{r.qty}</td>
                      <td className="px-3 py-2 whitespace-nowrap" data-testid={`upr-status-${i}`}>
                        <StatusBadge state={r.state} />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs" data-testid={`upr-dcno-${i}`}>{cell(r.dc_number)}</td>
                      <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-[#5A5F6E]">{cell(r.uploaded_at_ist)}</td>
                      <td className="px-3 py-2 text-xs">{cell(r.uploaded_by)}</td>
                      <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-[#5A5F6E]">{cell(r.picked_at_ist)}</td>
                      <td className="px-3 py-2 text-xs">{cell(r.picker)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3">
            <Button variant="outline" onClick={() => goPage(page - 1)} disabled={page <= 1} data-testid="upr-prev-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-[#5A5F6E]" data-testid="upr-page-info">Page {page} of {totalPages}</span>
            <Button variant="outline" onClick={() => goPage(page + 1)} disabled={page >= totalPages} data-testid="upr-next-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default UploadedPickSlipsReport;
